/**
 * MeetingPointPicker — Leaflet map picker para meeting points.
 *
 * Substitui inputs de texto (lat/lon manuais) por picker visual:
 *   1. Click no mapa → dropa pin nas coordenadas exactas
 *   2. Drag do pin → re-posiciona
 *   3. Reverse geocode Nominatim OSM → auto-preenche "Nome do local"
 *   4. Emite `onPick({lat, lng, address})` para o parent
 *
 * Uso:
 *   <MeetingPointPicker
 *     initialLat={39.5} initialLon={-8.0}
 *     initialName="" onPick={({lat,lng,address}) => { ... }}
 *   />
 */
import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix default marker icon in bundled builds (webpack) — a razão pela qual
// a maioria dos setups react-leaflet mostra um ícone partido em prod.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const orangePin = L.divIcon({
  html: `<div style="
    width:32px;height:32px;border-radius:50% 50% 50% 0;
    background:#E87A2A;border:3px solid #fff;
    transform:rotate(-45deg);
    box-shadow:0 4px 10px rgba(0,0,0,.4);
    display:flex;align-items:center;justify-content:center;
  "><span style="transform:rotate(45deg);font-size:14px;color:#fff;font-weight:900;">📍</span></div>`,
  className: 'meeting-point-icon',
  iconSize: [32, 32], iconAnchor: [16, 32],
});

// Component that installs click handler + fly-to on external position change.
const MapControl = ({ position, onMapClick }) => {
  const map = useMap();
  useMapEvents({
    click: (e) => onMapClick(e.latlng),
  });
  const first = useRef(true);
  useEffect(() => {
    if (position && first.current) {
      map.flyTo(position, 14, { duration: 0.6 });
      first.current = false;
    }
  }, [position, map]);
  return null;
};

const reverseGeocode = async (lat, lng) => {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=17&addressdetails=1&accept-language=pt`,
      { headers: { 'User-Agent': 'Tectur4x4/1.0' } },
    );
    if (!res.ok) return null;
    const data = await res.json();
    // Nomes preferidos: village > town > city > municipality > county
    const a = data.address || {};
    const short = a.village || a.town || a.city || a.hamlet || a.municipality
      || a.county || a.state || null;
    const display = data.display_name || null;
    return { short, display };
  } catch { return null; }
};

const MeetingPointPicker = ({
  initialLat = 39.5,
  initialLon = -8.0,
  initialName = '',
  height = 300,
  onPick,
  testIDPrefix = 'meeting-point',
}) => {
  const [pos, setPos] = useState(
    (typeof initialLat === 'number' && typeof initialLon === 'number' && !isNaN(initialLat))
      ? [initialLat, initialLon] : [39.5, -8.0],
  );
  const [name, setName] = useState(initialName || '');
  const [loadingGeo, setLoadingGeo] = useState(false);

  const dropPin = async (latlng) => {
    const lat = latlng.lat;
    const lng = latlng.lng;
    setPos([lat, lng]);
    setLoadingGeo(true);
    const geo = await reverseGeocode(lat, lng);
    setLoadingGeo(false);
    const auto = geo?.short || `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
    // Só sobrepõe se o user não tinha ainda escrito nada custom
    if (!name || name === initialName) setName(auto);
    onPick?.({ lat, lng, address: (geo?.short || null) });
  };

  const onNameChange = (v) => {
    setName(v);
    onPick?.({ lat: pos[0], lng: pos[1], address: v });
  };

  return (
    <div className="w-full space-y-2" data-testid={`${testIDPrefix}-container`}>
      <div
        style={{ height, borderRadius: 8, overflow: 'hidden', border: '1px solid rgba(196,162,117,0.35)' }}
        data-testid={`${testIDPrefix}-map`}
      >
        <MapContainer
          center={pos}
          zoom={14}
          style={{ width: '100%', height: '100%' }}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; OSM'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapControl position={pos} onMapClick={dropPin} />
          <Marker
            position={pos}
            icon={orangePin}
            draggable={true}
            eventHandlers={{
              dragend: (e) => {
                const ll = e.target.getLatLng();
                dropPin(ll);
              },
            }}
          />
        </MapContainer>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="text"
          placeholder="Nome do ponto de encontro"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          className="flex-1 px-3 py-2 rounded-md text-sm border"
          style={{ background: 'rgba(0,0,0,0.35)', color: '#F2EBDD', borderColor: 'rgba(196,162,117,0.35)' }}
          data-testid={`${testIDPrefix}-name-input`}
        />
        <div
          className="text-xs px-2 py-1 rounded font-mono"
          style={{ background: 'rgba(232,122,42,0.16)', color: '#E87A2A', borderWidth: 1, borderStyle: 'solid', borderColor: '#E87A2A' }}
          data-testid={`${testIDPrefix}-coords`}
        >
          📍 {pos[0].toFixed(5)}, {pos[1].toFixed(5)}
          {loadingGeo && ' · ⏳'}
        </div>
      </div>
      <p className="text-xs" style={{ color: '#8A7A63' }}>
        💡 Toca no mapa ou arrasta o pin para escolher o ponto de encontro. O nome é preenchido automaticamente.
      </p>
    </div>
  );
};

export default MeetingPointPicker;
